import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import type { DrawStroke, FramePoint, PlaybackState, Reaction, RoomSettings, VideoSource } from '@kinop/shared';
import { detectNativeProvider } from '@kinop/shared';
import { NativePlayer } from './NativePlayer.js';
import { DEFAULT_BRUSH, FrameOverlay, FrameToolbar, QUICK_EMOJIS } from './FrameTools.js';
import type { BrushSettings } from './DrawCanvas.js';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';

interface VideoPlayerProps {
  source: VideoSource | null;
  online: boolean;
  /** Нужен, чтобы собрать адрес проксирования через сервер. */
  roomCode: string;
  playback: PlaybackState | null;
  playbackRevision: number;
  serverNow: () => number;
  clockReady: boolean;
  canControl: boolean;
  /** Ведущий: он рисует даже с выключенным для остальных рисованием. */
  amIHost: boolean;
  settings: RoomSettings;
  reactions: Reaction[];
  strokes: DrawStroke[];
  onControl: (action: 'play' | 'pause' | 'seek' | 'rate', positionMs: number, rate?: number) => void;
  onDuration: (durationMs: number) => void;
  onPresence: (presence: {
    positionMs: number;
    isBuffering: boolean;
    localPaused: boolean;
    adPlaying: boolean;
  }) => void;
  /** Кто поставил общую паузу. */
  pausedBy: { clientId: string; nickname: string } | null;
  onRetryViaProxy: () => void;
  onReaction: (emoji: string, point: FramePoint) => void;
  onStroke: (stroke: Omit<DrawStroke, 'id' | 'authorClientId' | 'createdAt'>) => void;
  onUndoStroke: () => void;
  onClearStrokes: () => void;
}

/** Native providers own all playback controls. The site adds only room coordination and reactions. */
function VideoPlayerImpl(props: VideoPlayerProps): JSX.Element {
  const {
    source,
    roomCode,
    settings,
    reactions,
    strokes,
    serverNow,
    onRetryViaProxy,
    onReaction,
    onStroke,
    onUndoStroke,
    onClearStrokes,
    ...rest
  } = props;

  const [brush, setBrush] = useState<BrushSettings>(DEFAULT_BRUSH);
  const [drawMode, setDrawMode] = useState(false);
  const [lastEmoji, setLastEmoji] = useState(QUICK_EMOJIS[0]!);

  // Ведущий рисует всегда: запрет он ставит для остальных, а не для себя.
  // Именно ведущий, а не «кто может управлять»: в комнате, где управляют
  // все, второе верно для каждого, и запрет не действовал бы ни на кого.
  const canDraw = settings.drawing || props.amIHost;

  const handleBrushChange = useCallback((patch: Partial<BrushSettings>) => {
    setBrush((current) => ({ ...current, ...patch }));
  }, []);

  const handleQuickReaction = useCallback(
    (emoji: string) => {
      if (settings.reactions === false) return;
      setLastEmoji(emoji);
      // Из панели точку не выбирают — ставим в нижней половине кадра,
      // чтобы реакция всплыла через него, а не появилась под потолком.
      onReaction(emoji, { x: 0.2 + Math.random() * 0.6, y: 0.55 + Math.random() * 0.3 });
    },
    [onReaction, settings.reactions],
  );

  // Existing rooms can retain the old proxy flag. Recognized provider links
  // always use the provider's own iframe in the native-only release.
  const provider = useMemo(
    () => (source?.kind === 'url' ? detectNativeProvider(source.url) : null),
    [source?.kind, source?.url],
  );

  if (!source) {
    return (
      <div className="player player--empty">
        <Empty>
          <EmptyHeader>
            <EmptyMedia>🎬</EmptyMedia>
            <EmptyTitle>Видео ещё не выбрано</EmptyTitle>
            <EmptyDescription>
              Вставьте ссылку на YouTube, Rutube, VK Видео или Vimeo.
              Управляйте просмотром прямо в плеере площадки.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  const overlay = (
    <FrameOverlay
      reactions={settings.reactions === false ? [] : reactions}
      strokes={strokes}
      settings={settings}
      serverNow={serverNow}
      drawMode={drawMode}
      canDraw={canDraw}
      brush={brush}
      onStroke={onStroke}
    />
  );

  const extras = (
    <FrameToolbar
      settings={settings}
      canDraw={canDraw}
      drawMode={drawMode}
      brush={brush}
      onToggleDraw={() => setDrawMode((value) => !value)}
      onBrushChange={handleBrushChange}
      onReaction={handleQuickReaction}
      onUndo={onUndoStroke}
      onClear={onClearStrokes}
    />
  );

  if (provider) {
    return (
      <NativePlayer
        provider={provider}
        sourceUrl={source.url}
        serverNow={serverNow}
        overlay={overlay}
        extras={extras}
        autoCatchUp={settings.autoCatchUp}
        {...rest}
      />
    );
  }

  return <div className="player player--empty"><Empty><EmptyHeader><EmptyTitle>Выберите видео с площадки</EmptyTitle><EmptyDescription>Просмотр файлов и прямых потоков отключён. Вставьте ссылку на YouTube, Rutube, VK Видео или Vimeo во вкладке «Видео».</EmptyDescription></EmptyHeader></Empty></div>;

}

export const VideoPlayer = memo(VideoPlayerImpl);
